import { useEffect, useMemo, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useFieldById } from '/@/hooks/fields';
import { useRecommendationById } from '/@/hooks/my-recommendations/useFilterMyRecommendations';
import { updateActiveRecommendation } from '/@/store/recommendations/actions';
import { getAppInstance } from '/@/store/session/selectors';
import type { Recommendation } from '/@/types/recommendation';
import type { RecommendationVariant } from '/@/views/recommendations/use-save-recommendation';
import { resolveCropConfig, type StoredRecommendation, toRenderableRecommendation } from './parse-recommendation';

type SavedRecommendation = StoredRecommendation & {
    id: number;
    field?: number;
    userNode?: number;
    modified?: string;
    created?: string;
    commentsStatus?: Recommendation['commentsStatus'];
};

/**
 * Loads a stored recommendation and seeds it into the `activeRecommendation` slice.
 *
 * The v2 sections read their inputs from that slice — it's what lets the result page reuse the
 * wizard's components untouched instead of a read-only fork of each. Seeding is keyed on the
 * recommendation id rather than assuming the wizard left anything behind: it clears the slice
 * on unmount, and it unmounts while this view mounts, so a cold URL load and the hop straight
 * after "Terminar" have to take the same path.
 */
export const useSavedRecommendation = ({
    recommendationId,
    crop = 'corn',
}: {
    recommendationId: number;
    crop?: string;
}) => {
    const dispatch = useDispatch();
    const appInstance = useSelector(getAppInstance);
    const { data: recommendation } = useRecommendationById(recommendationId) as { data?: SavedRecommendation };

    const fieldId = recommendation?.field;
    const { data: field } = useFieldById({ id: fieldId as number }, { enabled: !!fieldId });

    const cropConfig = useMemo(() => resolveCropConfig(recommendation, crop), [recommendation, crop]);

    const seededFor = useRef<number | undefined>(undefined);
    useEffect(() => {
        if (!cropConfig || !fieldId || seededFor.current === recommendationId) return;
        seededFor.current = recommendationId;
        dispatch(
            updateActiveRecommendation({
                appInstance: appInstance.id,
                field: String(fieldId),
                crops: [cropConfig],
            })
        );
    }, [appInstance.id, dispatch, cropConfig, fieldId, recommendationId]);

    useEffect(() => {
        return () => {
            dispatch(updateActiveRecommendation(undefined));
        };
    }, [dispatch]);

    const parsedRecommendation = useMemo(
        // The stored record is untyped at the API boundary; `Recommendation` is what every
        // consumer of the blob (share, PDF) declares it wants.
        () =>
            recommendation
                ? (toRenderableRecommendation(recommendation, crop) as unknown as Recommendation)
                : undefined,
        [recommendation, crop]
    );

    return {
        recommendation,
        parsedRecommendation,
        field,
        cropConfig,
        // Read off the record rather than a stored flag: a management recommendation is the one
        // saved as `corn_density`, which is true of the ones the retired wizard saved too.
        variant: (recommendation?.cornDensity ? 'management' : 'hybrid') as RecommendationVariant,
        // The sections can't render before the slice holds this recommendation's config.
        isReady: !!field && seededFor.current === recommendationId,
    };
};
